/**
 * snowflake-setup.ts — Pure frontend SQL generator for Snowflake setup scripts.
 *
 * Generates Snowflake SQL to create a service user with key pair authentication,
 * appropriate role grants, and optional network policy. All identifier validation
 * happens at this boundary — callers pass raw strings.
 */

/** Regex for valid Snowflake unquoted identifiers (letters, digits, underscores, dollar signs). */
const SNOWFLAKE_IDENT = /^[A-Za-z_][A-Za-z0-9_$]*$/;

/** Regex for valid IPv4 addresses and CIDR ranges. */
const IP_CIDR = /^[\d./]+$/;

/** Regex for valid Base64 content (letters, digits, +, /, =, whitespace). */
const BASE64 = /^[A-Za-z0-9+/=\s]+$/;

/** Validate and uppercase a Snowflake identifier. Throws on invalid input. */
function assertIdent(name: string, label: string): string {
  const upper = name.trim().toUpperCase();
  if (!SNOWFLAKE_IDENT.test(upper)) {
    throw new Error(`${label}: only letters, numbers, underscores, dollar signs allowed`);
  }
  return upper;
}

export interface SetupSQLParams {
  username: string;
  publicKeyBody: string;
  warehouse: string;
  database: string;
  schema?: string;
  role?: string;
  readWrite?: boolean;
  /** Comma-separated egress IPs for network policy. Omit to skip network policy. */
  egressIPs?: string;
  /** Network policy name. Required when egressIPs is provided. */
  networkPolicyName?: string;
}

/**
 * Generate Snowflake setup SQL with:
 * - Unquoted uppercase identifiers (validated at boundary)
 * - USE ROLE transitions (USERADMIN for user/role, SECURITYADMIN for grants)
 * - FUTURE grants for both TABLES and VIEWS
 * - Optional network policy (conditional on egressIPs)
 * - DEFAULT_ROLE and DEFAULT_WAREHOUSE as bare identifiers
 */
export function generateSetupSQL(params: SetupSQLParams): string {
  const { publicKeyBody, readWrite = false, egressIPs, networkPolicyName } = params;

  // Validate publicKeyBody as Base64 before interpolating into SQL
  if (!BASE64.test(publicKeyBody)) {
    throw new Error('Invalid public key body: expected Base64');
  }

  const username = assertIdent(params.username, 'Username');
  const warehouse = assertIdent(params.warehouse, 'Warehouse');
  const database = assertIdent(params.database, 'Database');
  const schema = assertIdent(params.schema?.trim() ?? 'PUBLIC', 'Schema');
  const role = params.role?.trim() ? assertIdent(params.role, 'Role') : `${username}_ROLE`;

  const qualifiedSchema = `${database}.${schema}`;

  const readOnlyGrants = `GRANT SELECT ON ALL TABLES IN SCHEMA ${qualifiedSchema}
  TO ROLE ${role};
GRANT SELECT ON FUTURE TABLES IN SCHEMA ${qualifiedSchema}
  TO ROLE ${role};
GRANT SELECT ON ALL VIEWS IN SCHEMA ${qualifiedSchema}
  TO ROLE ${role};
GRANT SELECT ON FUTURE VIEWS IN SCHEMA ${qualifiedSchema}
  TO ROLE ${role};`;

  const readWriteGrants = `GRANT SELECT, INSERT, UPDATE, DELETE ON ALL TABLES IN SCHEMA ${qualifiedSchema}
  TO ROLE ${role};
GRANT SELECT, INSERT, UPDATE, DELETE ON FUTURE TABLES IN SCHEMA ${qualifiedSchema}
  TO ROLE ${role};
GRANT SELECT ON ALL VIEWS IN SCHEMA ${qualifiedSchema}
  TO ROLE ${role};
GRANT SELECT ON FUTURE VIEWS IN SCHEMA ${qualifiedSchema}
  TO ROLE ${role};
GRANT CREATE TABLE, CREATE VIEW, CREATE TEMPORARY TABLE ON SCHEMA ${qualifiedSchema}
  TO ROLE ${role};`;

  const tableGrants = readWrite ? readWriteGrants : readOnlyGrants;

  // Build network policy section if egress IPs are provided
  let networkSection = '';
  if (egressIPs?.trim()) {
    const ips = egressIPs
      .split(',')
      .map((ip) => ip.trim())
      .filter(Boolean);

    // Validate each IP/CIDR before interpolating into SQL
    for (const ip of ips) {
      if (!IP_CIDR.test(ip)) {
        throw new Error(`Invalid IP address: ${ip}`);
      }
    }

    const valueList = ips.map((ip) => `'${ip}'`).join(', ');
    const policyName = networkPolicyName ? assertIdent(networkPolicyName, 'Network policy') : 'RUDDER_AI';
    const ruleName = `${qualifiedSchema}.${username}_NETWORK_RULE`;

    networkSection = `
-- Step 5: Network policy (restrict to known IPs)
USE ROLE ACCOUNTADMIN;
CREATE OR REPLACE NETWORK RULE ${ruleName}
  TYPE = IPV4
  MODE = INGRESS
  VALUE_LIST = (${valueList})
  COMMENT = 'Rudder AI egress IPs';

CREATE NETWORK POLICY IF NOT EXISTS ${policyName}
  ALLOWED_NETWORK_RULE_LIST = ('${ruleName}')
  COMMENT = 'Restrict Rudder AI service user to known IPs';

ALTER USER ${username}
  SET NETWORK_POLICY = ${policyName};
`;
  }

  return `-- ============================================================
-- Rudder AI -- Snowflake Setup
-- Requires ACCOUNTADMIN privileges. Run in a Snowflake worksheet.
-- ============================================================

-- Step 1: Create role and user
USE ROLE USERADMIN;
CREATE ROLE IF NOT EXISTS ${role};
CREATE USER IF NOT EXISTS ${username}
  TYPE = SERVICE
  DEFAULT_ROLE = ${role}
  DEFAULT_WAREHOUSE = ${warehouse}
  RSA_PUBLIC_KEY = '${publicKeyBody}';

-- Step 2: Grant role hierarchy + access
USE ROLE SECURITYADMIN;
GRANT ROLE ${role} TO ROLE SYSADMIN;
GRANT ROLE ${role} TO USER ${username};
GRANT USAGE ON WAREHOUSE ${warehouse} TO ROLE ${role};
GRANT USAGE ON DATABASE ${database} TO ROLE ${role};
GRANT USAGE ON SCHEMA ${qualifiedSchema} TO ROLE ${role};

-- Step 3: Grant table + view access (${readWrite ? 'read-write' : 'read-only'})
${tableGrants}
${networkSection}-- NOTE: For multiple schemas, repeat the GRANT USAGE ON SCHEMA and
-- table/view grant blocks for each additional schema.

-- Step ${egressIPs?.trim() ? '6' : '4'}: Verify (run after setup)
DESC USER ${username};
-- Check that RSA_PUBLIC_KEY_FP is set (non-null).
`;
}
